import { Row, Col, Button } from "reactstrap";
import { Tooltip, Whisper } from "rsuite";
import { useRef } from "react";

import InputFormatter from "../../components/Records/Formatters/InputFormatter";
import FieldFormatter from "../../components/Records/Formatters/FieldFormatter";
import { ReusableViewReferenceFields } from "../../types/PageInterfaces";
import SummaryCardWidget from "./SummaryCardWidget/SummaryCardWidget";
import { generateTimePrefills } from "../../utils/QuickCreateUtils";
import SubitemRenderer from "../Records/Subitems/SubitemRenderer";
import { Field, Module } from "../../types/TenantInterfaces";
import Required from "../../elements/Required";
import URLPreview from "./URLPreview";

interface RecordSummaryWidgetProps {
  module: Module;
  section: any;
  createMode: boolean;
  values: any;
  handleChange: any;
  record: any;
  marginBottom: any;
  useCard: any;
  readOnly: any;
  isCard: boolean;
  parentRecord: any;
  headerOptions: any;
  convertFields?: (field: any) => any;
  /**
   * Payload property to pass it to SubitemRenderer to display the date added to it.
   */
  payload?: any;
  reusableViewReferenceFields?: ReusableViewReferenceFields;
}

export const RecordSummaryWidget = ({
  module,
  section,
  convertFields,
  createMode,
  values,
  handleChange,
  record,
  marginBottom,
  useCard,
  readOnly,
  isCard,
  parentRecord = null,
  headerOptions,
  payload,
  reusableViewReferenceFields,
}: RecordSummaryWidgetProps) => {
  return (
    <div>
      {section.Title && !isCard && (
        <div className="py-2 mb-3 section-header">
          <div className="d-flex align-items-center justify-content-between w-100 flex-wrap">
            <div>
              {section.Title && <h3 className="mb-0">{section.Title}</h3>}
              {section.Description && (
                <p className="mb-0 small text-muted">{section.Description}</p>
              )}
            </div>
            <div className="d-flex">{headerOptions}</div>
          </div>
        </div>
      )}
      <Row className={`align-items-top ${marginBottom ? "mb-3" : ""}`}>
        {section?.Fields?.length > 0 && (
          <>
            {section.Fields.map(convertFields ? convertFields : () => true).map(
              (f: any, i: number) => {
                return (
                  <SectionField
                    key={i}
                    f={f}
                    i={i}
                    module={module}
                    record={record}
                    createMode={createMode}
                    values={values}
                    handleChange={handleChange}
                    isCard={isCard}
                    useCard={useCard}
                    parentRecord={parentRecord}
                    readOnly={readOnly}
                    section={section}
                    payload={payload}
                    reusableViewReferenceFields={reusableViewReferenceFields}
                  />
                );
              }
            )}
          </>
        )}
      </Row>
    </div>
  );
};

interface SectionFieldProps {
  f: any;
  i: number;
  module: Module;
  record: any;
  createMode: boolean;
  values: any;
  handleChange: any;
  isCard: boolean;
  useCard: any;
  parentRecord: any;
  readOnly: any;
  section: any;
  payload?: any;
  reusableViewReferenceFields?: ReusableViewReferenceFields;
}

const SectionField = ({
  f,
  i,
  module,
  record,
  createMode,
  values,
  handleChange,
  isCard,
  useCard,
  parentRecord,
  readOnly,
  section,
  payload,
  reusableViewReferenceFields,
}: SectionFieldProps) => {
  const field = f.Field;
  const fieldAPIName = field?.APIName;

  const inputRef = useRef<HTMLInputElement>(null);

  if (!field || (createMode && field?.SystemField && field?.ReadOnly)) {
    return null;
  }

  field.ModuleAPIName = module.APIName;

  if (!isCard && useCard && !createMode && field?.RecordSummary === true) {
    const keys = Object.keys(field.RecordSummaryFields);

    if (
      field?.RecordSummaryFields?.[keys?.[0]]?.summaryPresentation !==
      "RegularField"
    ) {
      return (
        <Col className="mb-3" xs="12">
          <SummaryCardWidget
            records={
              record[fieldAPIName]
                ? Array.isArray(record[fieldAPIName])
                  ? record[fieldAPIName]
                  : [record[fieldAPIName]]
                : null
            }
            fieldSchema={field}
            parentRecord={record}
            reusableViewReferenceFields={reusableViewReferenceFields}
          />
        </Col>
      );
    }
  }

  if (field?.FieldType === "Subitem" || field?.FieldType === "MultiSubitem") {
    return (
      <Col key={i} className="mb-3" xs={"12"} sm={"12"} md={"12"}>
        <Row className="align-items-center">
          <Col xs="12" sm="12" md="12" className="pb-1 text-left">
            {createMode ? (
              <>
                <SubitemRenderer
                  module={module}
                  field={field}
                  subItemValues={
                    payload?.[field?.APIName] ?? calculateDefault(field)
                  }
                  defaultSub={{}}
                  handleChange={handleChange}
                  disableReadonlyFields={false}
                  prefill={null}
                  reusableViewReferenceFields={reusableViewReferenceFields}
                />
              </>
            ) : (
              <>
                <SummaryCardWidget
                  records={
                    record[fieldAPIName]
                      ? Array.isArray(record[fieldAPIName])
                        ? record[fieldAPIName]
                        : [record[fieldAPIName]]
                      : null
                  }
                  module={module}
                  fieldSchema={field}
                  parentRecord={record}
                  readOnly={false}
                  reusableViewReferenceFields={reusableViewReferenceFields}
                />
              </>
            )}
          </Col>
        </Row>
      </Col>
    );
  }

  return (
    <Col
      key={i}
      className="mb-3"
      xs={isCard ? "6" : "12"}
      sm={isCard ? "6" : "12"}
      md={field.ColSpan === 2 ? "12" : "6"}
    >
      <Row className="align-items-center">
        <Col xs="12" sm="12" md="12" className="pb-1 text-left">
          <Row className="align-items-center">
            <Col xs="" className="pb-2">
              <label
                onClick={() => {
                  inputRef?.current?.focus();
                }}
                htmlFor={`record-input-${field.APIName}`}
                className={`cursor-pointer font-weight-500 text-dark small my-0`}
              >
                {field?.Name ? field.Name + ":" : ""}
                {field?.GcWebsite && (
                  <URLPreview url={record?.[field?.APIName]?.[0]?.URL} />
                )}
              </label>
            </Col>
            <Col xs="auto">
              {field?.Description && (
                <Whisper
                  speaker={
                    <Tooltip
                      style={{
                        minWidth: 200,
                      }}
                      className="text-left"
                    >
                      {field.Description}
                    </Tooltip>
                  }
                  trigger="hover"
                  placement="autoHorizontal"
                  controlId={`tooltip_detail_${fieldAPIName}`}
                >
                  <Button
                    color="secondary"
                    className="btn-icon-only mr-0"
                    style={{
                      position: "relative",
                      top: 0,
                      height: "1.2rem",
                      width: "1.2rem",
                    }}
                    outline
                    size="sm"
                    tabIndex={-1}
                  >
                    <i
                      className="mdi mdi-information-outline"
                      style={{
                        fontSize: 18,
                        lineHeight: 1,
                        position: "relative",
                        top: 0,
                        left: 0,
                      }}
                    />
                  </Button>
                </Whisper>
              )}
              {createMode && field?.Required && <Required />}
            </Col>
          </Row>
        </Col>
        <Col xs="12" sm="12" md="12" style={{ fontSize: 14 }} className="mt--2">
          {createMode &&
          field?.FieldType !== "MultiSubitem" &&
          field?.FieldType !== "Subitem" ? (
            <>
              <InputFormatter
                ref={inputRef}
                module={module}
                field={field}
                value={calculateDefault(field)}
                onChange={handleChange}
                disabled={
                  readOnly ||
                  (field.ReadOnly && values[fieldAPIName] !== undefined) ||
                  (field.SystemField && field.ReadOnly)
                }
                reusableViewReferenceFields={reusableViewReferenceFields}
              />
            </>
          ) : (
            <>
              {field && (
                <div>
                  <FieldFormatter
                    ref={inputRef}
                    field={field}
                    value={record ? record[fieldAPIName] : null}
                    record={record}
                    recordID={record?._id || record?.id}
                    moduleAPIName={module?.APIName}
                    forceReadOnly={readOnly}
                    subitemParentID={
                      section?.Type === "MultiSubitem" ||
                      section?.Type === "Subitem"
                        ? parentRecord?.id
                        : null
                    }
                    multiSubitemID={record?.id || record?._id}
                    reusableViewReferenceFields={reusableViewReferenceFields}
                  />
                </div>
              )}
            </>
          )}
        </Col>
      </Row>
    </Col>
  );

  // Handle Dynamic defaults values
  function calculateDefault(field: Field) {
    const defaultValue = values ? values[field?.APIName] : field?.DefaultValue;

    if (defaultValue?.startsWith?.("{{time.")) {
      const datePrefill = generateTimePrefills(defaultValue, true);
      const newDefaultValue = datePrefill?.Value || datePrefill?.Max;
      return new Date(newDefaultValue ?? "").toISOString();
    }

    return defaultValue;
  }
};
